import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getWalletPortfolioCoin } from "../api/endpoints/wallet/wallet";
import { getTradingSwapCoins } from "../api/endpoints/trading/trading";
import type { PortfolioCoin } from "../api/models/portfolioCoin";
import { getBearerToken } from "../lib/authClient";
import { getCoins } from "../lib/marketApi";
import { toNumber } from "../lib/numberFormat";

import "./CoinPortfolioPage.css";

interface CoinMeta {
  code?: string;
  name?: string;
  icon?: string;
}

const formatUSD = (v: string | number) =>
  toNumber(v).toLocaleString("pt-BR", {
    style: "currency",
    currency: "USD",
  });

const formatQty = (v: string | number) =>
  toNumber(v).toLocaleString("pt-BR", {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8,
  });

const formatSignedPct = (v: string | number) => {
  const n = toNumber(v);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
};

export default function CoinPortfolioPage() {
  const navigate = useNavigate();
  const params = useParams<{ coin: string }>();
  const location = useLocation();
  const coinCode = (params.coin ?? "").toUpperCase();
  const meta = (location.state as CoinMeta | null) ?? null;

  const [data, setData] = useState<PortfolioCoin | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marketIcon, setMarketIcon] = useState<string | null>(null);
  const [marketName, setMarketName] = useState<string | null>(null);

  useEffect(() => {
    if (!coinCode) return;
    let cancelled = false;

    const fetchCoin = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getBearerToken();
        const response = await getWalletPortfolioCoin(
          coinCode,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );
        if (cancelled) return;
        if (response.status === 200 && "data" in response.data) {
          setData(response.data.data);
        } else {
          setError(`Falha ao buscar portfólio (status ${response.status}).`);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Erro ao buscar portfolio coin:", err);
        setError(err instanceof Error ? err.message : "Erro desconhecido");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCoin();
    return () => {
      cancelled = true;
    };
  }, [coinCode]);

  useEffect(() => {
    if (meta?.icon && meta?.name) return;
    let cancelled = false;
    const fetchMeta = async () => {
      // 1. tenta em /market/coins (base coins: BTC, ETH, SOL...)
      try {
        const coins = await getCoins();
        if (cancelled) return;
        const found = coins.find((c) => c.code === coinCode);
        if (found) {
          setMarketIcon(found.icon ?? null);
          setMarketName(found.name ?? null);
          return;
        }
      } catch {}

      // 2. fallback: busca nas moedas de swap (cobre USDT e outras quote coins)
      try {
        const token = getBearerToken();
        const opts = token
          ? { headers: { Authorization: `Bearer ${token}` } }
          : undefined;
        // consulta o que pode ser convertido PARA coinCode — a lista inclui sua metadata
        const res = await getTradingSwapCoins(
          { coin: coinCode, side: "to" },
          opts,
        );
        if (cancelled) return;
        // a resposta retorna pares de destino; tentamos também via "from"
        if (res.status !== 200 || !("data" in res.data)) {
          const res2 = await getTradingSwapCoins(
            { coin: "BTC", side: "to" },
            opts,
          );
          if (cancelled) return;
          if (res2.status === 200 && "data" in res2.data) {
            const match = res2.data.data.find((c) => c.coin === coinCode);
            if (match) {
              setMarketIcon(match.icon);
              setMarketName(match.name);
            }
          }
          return;
        }
        // coinCode é uma moeda "from" válida — pega ícone da primeira moeda "to"
        // e tenta achar coinCode via "from" de outra moeda
        const toCoins = res.data.data;
        if (toCoins.length > 0) {
          const res2 = await getTradingSwapCoins(
            { coin: toCoins[0].coin, side: "from" },
            opts,
          );
          if (cancelled) return;
          if (res2.status === 200 && "data" in res2.data) {
            const match = res2.data.data.find((c) => c.coin === coinCode);
            if (match) {
              setMarketIcon(match.icon);
              setMarketName(match.name);
            }
          }
        }
      } catch {}
    };
    fetchMeta();
    return () => { cancelled = true; };
  }, [coinCode, meta?.icon, meta?.name]);

  const coinIcon = meta?.icon ?? marketIcon;
  const coinName = meta?.name ?? marketName;

  const perfUp = useMemo(() =>
    data ? toNumber(data.performancePercent) >= 0 : true, [data]);

  return (
    <div className="page-centered">
      <div className="coinPortfolio-wrap">
        <button
          type="button"
          className="coinPortfolio-back"
          onClick={() => navigate(-1)}
        >
          ← Voltar
        </button>

        {loading && (
          <div className="coinPortfolio-status">Carregando portfólio...</div>
        )}

        {error && !loading && (
          <div className="coinPortfolio-status coinPortfolio-status--error">
            {error}
          </div>
        )}

        {data && !loading && (
          <>
            <div className="coinPortfolio-hero">
              <div className="coinPortfolio-heroHead">
                <div className="coinPortfolio-heroIcon">
                  {coinIcon
                    ? <img src={coinIcon} alt={coinName ?? data.coin} />
                    : data.coin.slice(0, 3)}
                </div>
                <div className="coinPortfolio-heroName">
                  {coinName ?? data.coin}
                </div>
              </div>

              <div className="coinPortfolio-heroAmount">
                <span className="coinPortfolio-heroAmount-approx">≈</span>
                {formatUSD(data.totalUSD)}
              </div>

              <div className="coinPortfolio-heroCard">
                <div className="coinPortfolio-heroCardCell">
                  <div className="coinPortfolio-heroCardLabel">
                    Saldo
                  </div>
                  <div className="coinPortfolio-heroCardValue">
                    {formatQty(data.quantity)} {data.coin}
                  </div>
                </div>
                <div className="coinPortfolio-heroCardCell">
                  <div className="coinPortfolio-heroCardLabel">Performance</div>
                  <div
                    className={`coinPortfolio-heroCardValue ${
                      perfUp
                        ? "coinPortfolio-heroCardValue--up"
                        : "coinPortfolio-heroCardValue--down"
                    }`}
                  >
                    {perfUp ? "↑" : "↓"} {formatSignedPct(data.performancePercent)}
                  </div>
                </div>
              </div>
            </div>

            <div className="coinPortfolio-details">
              <div className="coinPortfolio-detailRow">
                <span className="coinPortfolio-detailLabel">
                  Disponível p/ converter/sacar
                </span>
                <span className="coinPortfolio-detailValue">
                  {formatQty(data.available)} {data.coin}
                  {toNumber(data.available) < toNumber(data.quantity) && (
                    <span className="coinPortfolio-detailHint">
                      {formatQty(toNumber(data.quantity) - toNumber(data.available))} em ordens/margem
                    </span>
                  )}
                </span>
              </div>
              <div className="coinPortfolio-detailRow">
                <span className="coinPortfolio-detailLabel">
                  Preço médio de compra
                </span>
                <span className="coinPortfolio-detailValue">
                  {formatUSD(data.averageBuyPrice)}
                </span>
              </div>
              <div className="coinPortfolio-detailRow">
                <span className="coinPortfolio-detailLabel">Total investido</span>
                <span className="coinPortfolio-detailValue">
                  {formatUSD(data.totalInvested)}
                </span>
              </div>
              <div className="coinPortfolio-detailRow">
                <span className="coinPortfolio-detailLabel">Performance</span>
                <span className="coinPortfolio-detailValue">
                  <span
                    className={perfUp
                      ? "coinPortfolio-detailValue-accent--up"
                      : "coinPortfolio-detailValue-accent--down"}
                  >
                    ({formatSignedPct(data.performancePercent)})
                  </span>
                  {formatUSD(data.performance)}
                </span>
              </div>
            </div>

            <div className="coinPortfolio-actions">
              <button
                type="button"
                className="coinPortfolio-btn coinPortfolio-btn--primary"
                onClick={() =>
                  navigate(`/wallet/deposit/${data.coin}`, {
                    state: {
                      code: data.coin,
                      name: coinName,
                      icon: coinIcon,
                    },
                  })}
              >
                Depositar
              </button>
              <button
                type="button"
                className="coinPortfolio-btn coinPortfolio-btn--primary"
                onClick={() =>
                  navigate(`/wallet/withdraw/${data.coin}`, {
                    state: {
                      code: data.coin,
                      name: coinName,
                      icon: coinIcon,
                    },
                  })}
              >
                Sacar
              </button>
            </div>

            <div className="coinPortfolio-tradeGrid">
              {(
                [
                  { key: "market", label: "Spot market" },
                  { key: "limit", label: "Spot limit" },
                  { key: "swap", label: "Swap" },
                  { key: "future", label: "Future" },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className="coinPortfolio-btn coinPortfolio-btn--primary"
                  onClick={() =>
                    navigate(`/trade/${data.coin === "USDT" ? "BTCUSDT" : `${data.coin}USDT`}/${opt.key}`, {
                      state: {
                        code: data.coin,
                        name: meta?.name,
                        icon: meta?.icon,
                      },
                    })}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
